import _ from 'lodash'

import {
  MODEM_REQUIRED,
  MODEM_ADDRESS_SAME_AS_INSTALL,
  MODEM_ADDRESS_MANUAL,
  MODEM_SET_FIELDS,
  MODEM_SET_ERRORS,
  MODEM_CLEAR_ERROR,
  MODEM_RESPONSE_MESSAGE,
  MODEM_DELIVERY_SUCCESS,
} from 'containers/stages/ModemStage/actions'

import {
  PERSONAL_SET_FIELDS,
  PERSONAL_FIELD_ERRORS,
  PERSONAL_SUBMIT_SUCCESS,
  PERSONAL_CLEAR_FIELD_ERROR,
} from 'containers/stages/PersonalInfoStage/actions'

import {
  IDENTIFICATION_INIT_LOADING,
  IDENTIFICATION_INIT_DONE,
  IDENTIFICATION_ERROR,
  IDENTIFICATION_SUCCESS,
} from 'containers/stages/IdentificationStage/actions'
import { PLAN_SELECT_SUCCESS } from 'containers/stages/PlanSelectionStage/actions'
import {
  PLAN_SET_FIELDS,
  PLAN_SET_ERRORS,
  LOAD_VOUCHER_SUCCESS,
  LOAD_VOUCHER_FAILURE,
  LOAD_VOUCHER_REQUEST,
  VOUCHER_CODE,
  CLEAR_VOUCHER,
} from 'containers/stages/PlanSelectionStage/constants'
import {
  MULTI_ADDRESS_SHOW_MORE_ADDRESSES,
  MULTI_ADDRESS_SELECT_SINGLE_OF_MULTIPLE,
  MULTI_ADDRESS_UPDATE_SUCCESS,
  MULTI_ADDRESS_LOADED,
} from 'containers/stages/MultipleAddressStage/actions'

import {
  OWNER_FIELD_ERRORS,
  OWNER_SUBMIT_SUCCESS,
  OWNER_CLEAR_FIELD_ERROR,
} from 'containers/stages/OwnerCheckStage/actions'
import { SUBMIT_LEAD_SUCCESS } from 'containers/stages/SubmitStage/actions'
import {
  ADDRESS_CHECK_INIT,
  ADDRESS_CHECK_SUCCESS,
  ADDRESS_CHECK_FAILED,
  ADDRESS_CHECK_ERROR,
  ADDRESS_CHECK_MULTIPLE,
  ADDRESS_CHECK_TASK_QUEUED,
} from 'containers/stages/AddressCheckStage/actions'
import {
  IS_LOADING,
  SET_ERROR,
  PAYMENT_PROCESSING,
  PAYMENT_ERROR,
  PAYMENT_DECLINED,
  PAYMENT_SUCCESS,
  ORDER_SUMMARY_FETCH_SUCCESS,
  ORDER_SUMMARY_FETCH_FAILURE,
  STAGE_TRANSITION_SUCCESS,
} from './actions'

import {
  DEFAULT_MULTI_ADDRESS_STAGE_NUMBER_OF_ADDRESSES_TO_SHOW,
  DEFAULT_DETAILS_PROVIDED,
} from './constants'

const DEFAULT_APP_SCHEMA = {
  isLoading: false,
  error: false,
  currentStage: null,
  // Personal stage
  personalFieldError: {},
  personalFieldValues: DEFAULT_DETAILS_PROVIDED,
  personalFieldProvided: {},
  // Payment stage
  paymentError: false,
  paymentDeclined: false,
  paymentProcessing: false,
  // Address Check data
  addressTaskQueued: false,
  addressResult: '',
  addressAvailabilityDate: null,
  // Multiple Address stage
  multiAddressStageAddresses: [],
  multiAddressStageSelectedId: null,
  multiAddressStageNumAddressesToShow: DEFAULT_MULTI_ADDRESS_STAGE_NUMBER_OF_ADDRESSES_TO_SHOW,
  // Install address
  installAddress: [],
  installAddressClass: null,
  // Product Feature Availability
  productFeatureAvailability: null,
  // Owner check data
  ownsService: false,
  ownerFieldError: {},
  ownerAskLandline: false,
  // Identity/Green Id
  identityVerified: {},
  identificationVerificationToken: null,
  // Plan Selection
  planFields: {},
  planErrors: {},
  voucher: '',
  // Modem Delivery
  modemRequired: false,
  modemSameAsInstallAddress: true,
  modemManualAddress: false,
  modemFields: {},
  modemErrors: {},
  modemResponseMessage: '',
  // Finalised delivery address
  modemDeliveryAddress: '',
  // Success stage
  reference: false,
}

export default function (state = DEFAULT_APP_SCHEMA, action) {
  switch (action.type) {
    case IS_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      }
    case SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }

    case MODEM_REQUIRED:
      return {
        ...state,
        modemRequired: action.payload,
      }

    case MODEM_ADDRESS_SAME_AS_INSTALL:
      return {
        ...state,
        modemSameAsInstallAddress: action.payload,
      }

    case MODEM_ADDRESS_MANUAL:
      return {
        ...state,
        modemManualAddress: action.payload,
      }

    case MODEM_SET_FIELDS:
      return {
        ...state,
        modemFields: {
          ...state.modemFields,
          ...action.payload,
        },
        // and clear any errors for updated fields
        modemErrors: _.omit(state.modemErrors, Object.keys(action.payload)),
      }

    case MODEM_SET_ERRORS:
      return {
        ...state,
        modemErrors: {
          ...state.modemErrors,
          ...action.payload,
        },
      }

    case MODEM_RESPONSE_MESSAGE:
      return {
        ...state,
        modemResponseMessage: action.payload,
      }

    case MODEM_CLEAR_ERROR:
      if (!state.modemErrors[action.payload]) return state
      return {
        ...state,
        modemErrors: _.omit(state.modemErrors, action.payload),
      }

    case MODEM_DELIVERY_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentStage: action.payload.next,
        modemDeliveryAddress: action.payload.address,
      }

    // Plan Selection
    case PLAN_SELECT_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentStage: action.payload.nextStage,
        planSelected: action.payload.plan,
      }

    case PLAN_SET_FIELDS: {
      return {
        ...state,
        planFields: {
          ...state.planFields,
          ...action.payload,
        },
      }
    }
    case PLAN_SET_ERRORS: {
      const { voucher: _1, ...rest } = state
      return {
        ...rest,
        planErrors: {
          ...state.planErrors,
          ...action.payload,
        },
      }
    }
    case CLEAR_VOUCHER: {
      const { voucher: _2, ...rest } = state
      return { ...rest }
    }
    case LOAD_VOUCHER_REQUEST:
      return { ...state, voucherLoading: true }

    case LOAD_VOUCHER_FAILURE:
      return {
        ...state,
        voucherLoading: false,
      }

    case LOAD_VOUCHER_SUCCESS:
      return {
        ...state,
        voucherLoading: false,
        voucher: action.payload.code,
        planSelected: action.payload.plan,
        planErrors: _.omit(state.planErrors, VOUCHER_CODE),
      }

    // Identification
    case IDENTIFICATION_INIT_LOADING:
      return {
        ...state,
        identificationVerificationToken: null,
        error: false,
      }
    case IDENTIFICATION_INIT_DONE:
      return {
        ...state,
        identificationVerificationToken: action.verificationToken,
        isLoading: false,
        currentStage: 'identification',
      }
    case IDENTIFICATION_SUCCESS:
      return {
        ...state,
        currentStage: 'payment',
        identityVerified: true,
        identificationVerificationToken: null,
      }
    case IDENTIFICATION_ERROR:
      return {
        ...state,
        identificationError: action.payload,
      }
    // Payment
    case PAYMENT_SUCCESS:
      return {
        ...state,
        paymentProcessing: false,
        currentStage: action.payload,
      }
    case PAYMENT_PROCESSING:
      return {
        ...state,
        paymentProcessing: true,
        paymentError: false,
        paymentDeclined: false,
      }
    case PAYMENT_ERROR:
      return {
        ...state,
        paymentError: action.payload.error,
        paymentProcessing: false,
      }
    case PAYMENT_DECLINED:
      return {
        ...state,
        paymentDeclined: action.payload.declined,
        paymentProcessing: false,
      }
    case PERSONAL_SET_FIELDS:
      return {
        ...state,
        isLoading: false,
        personalFieldValues: action.payload,
      }
    case PERSONAL_FIELD_ERRORS:
      return {
        ...state,
        personalFieldError: action.payload,
        isLoading: false,
      }
    case PERSONAL_CLEAR_FIELD_ERROR:
      if (!state.personalFieldError[action.payload]) return state
      return {
        ...state,
        personalFieldError: _.omit(state.personalFieldError, action.payload),
      }
    case PERSONAL_SUBMIT_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentStage: action.payload.next,
      }
    case ADDRESS_CHECK_INIT:
      return {
        ...state,
        isLoading: true,
        addressTaskQueued: false,
        addressResult: '',
      }
    case ADDRESS_CHECK_TASK_QUEUED:
      return {
        ...state,
        addressTaskQueued: true,
      }
    case ADDRESS_CHECK_FAILED:
      return {
        ...state,
        isLoading: false,
        addressTaskQueued: false,
        addressResult: action.payload.reason,
        addressAvailabilityDate: action.payload.date,
      }
    case ADDRESS_CHECK_SUCCESS:
      return {
        ...state,
        isLoading: false,
        addressTaskQueued: false,
        currentStage: action.payload.nextStage,
        ownerAskLandline: action.payload.askLandline,
        appointmentRequired: action.payload.appointmentRequired,
        addressResult: 'success',
        installAddress: action.payload.installAddress,
        installAddressClass: action.payload.serviceClass,
        productFeatureAvailability: action.payload.productFeatureAvailability,
      }
    case ADDRESS_CHECK_ERROR:
      return {
        ...state,
        isLoading: false,
        addressTaskQueued: false,
        addressResult: 'error',
      }
    case ADDRESS_CHECK_MULTIPLE:
      return {
        ...state,
        isLoading: false,
        addressTaskQueued: false,
        multiAddressStageAddresses: action.payload.addresses,
        currentStage: action.payload.nextStage,
        addressResult: 'success',
      }
    case MULTI_ADDRESS_SELECT_SINGLE_OF_MULTIPLE:
      return {
        ...state,
        isLoading: false,
        multiAddressStageSelectedId: action.payload,
      }
    case MULTI_ADDRESS_SHOW_MORE_ADDRESSES:
      return {
        ...state,
        isLoading: false,
        multiAddressStageNumAddressesToShow: state.multiAddressStageNumAddressesToShow + 5,
      }
    case MULTI_ADDRESS_UPDATE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentStage: action.payload.nextStage,
        installAddress: action.payload.address,
        addressResult: '',
      }
    case MULTI_ADDRESS_LOADED:
      return {
        ...state,
        multiAddressStageAddresses: action.payload,
        isLoading: false,
      }
    case OWNER_FIELD_ERRORS:
      return {
        ...state,
        ownerFieldError: action.payload,
        isLoading: false,
      }
    case OWNER_CLEAR_FIELD_ERROR:
      if (!state.ownerFieldError[action.payload]) return state
      return {
        ...state,
        ownerFieldError: _.omit(state.ownerFieldError, action.payload),
      }
    case OWNER_SUBMIT_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentStage: action.payload.nextStage,
        ownsService: action.payload.isOwner,
      }
    case SUBMIT_LEAD_SUCCESS:
      return {
        ...state,
        currentStage: action.payload.next,
        reference: action.payload.reference,
      }
    case ORDER_SUMMARY_FETCH_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reference: action.payload.reference,
        appointmentRequired: action.payload.appointment_required,
        currentStage: action.payload.current_stage,
        installAddress: action.payload.install_address,
        installAddressClass: action.payload.install_address_class,
        modemRequired: action.payload.modem_required,
        modemDeliveryAddress: action.payload.delivery_address,
        modemAddressSameAsDelivery: action.payload.delivery_same_as_install,
        ownsService: action.payload.owns_service,
        planSelected: action.payload.plan_selected,
        personalFieldValues: {
          ...action.payload.personal_details,
          addressText: action.payload.delivery_address,
        },
        email: action.payload.email,
        identityVerified: action.payload.identity_verified,
        voucher: action.payload.voucher,
        productFeatureAvailability: action.payload.product_feature_availability,
      }
    }
    case ORDER_SUMMARY_FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: true,
      }
    case STAGE_TRANSITION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        currentStage: action.payload.next,
      }
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./containers/Checkout/reducer.js